import React, { useState, useEffect } from "react";
import { useI18n } from "../contexts/I18nContext";
import { api } from "../utils/api";
import PhotoUpload from "./PhotoUpload";

export default function ProDashboard() {
  const { t } = useI18n();
  const [places, setPlaces] = useState([]);
  const [claims, setClaims] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    async function load() {
      try {
        const [placesData, claimsData] = await Promise.all([
          api("/api/claims/pro/places").catch(() => ({ places: [] })),
          api("/api/claims/my").catch(() => ({ claims: [] })),
        ]);
        setPlaces(placesData.places || []);
        setClaims(claimsData.claims || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function startEdit(place) {
    setEditing(place.id);
    setForm({
      name: place.name || "",
      description: place.description || "",
      city: place.city || "",
      address: place.address || "",
      phone: place.phone || "",
      website: place.website || "",
      opening_hours: place.opening_hours || "",
      price_range: place.price_range || "",
      ticketing_url: place.ticketing_url || "",
      features: place.features || [],
    });
  }

  async function handleSave(placeId) {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const data = await api(`/api/claims/pro/places/${placeId}`, {
        method: "PUT",
        body: JSON.stringify(form),
      });
      setPlaces((prev) => prev.map((p) => (p.id === placeId ? data.place : p)));
      setEditing(null);
      setMessage({ type: "success", text: "Lieu mis à jour !" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-white">🏢 Dashboard Pro</h2>
        <p className="mt-1 text-sm text-sensoria-text-dim">Gérez vos établissements revendiqués.</p>
      </div>

      {message.text && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${
          message.type === "success" ? "border-sensoria-success/30 bg-sensoria-success/10 text-sensoria-success" : "border-sensoria-danger/30 bg-sensoria-danger/10 text-sensoria-danger"
        }`}>{message.text}</div>
      )}

      {/* Claimed places */}
      {places.length > 0 ? (
        <div className="space-y-4">
          {places.map((place) => (
            <div key={place.id} className="sensoria-card">
              {editing === place.id ? (
                /* Edit mode */
                <div className="space-y-4">
                  <h3 className="font-display text-lg font-semibold text-white">Modifier : {place.name}</h3>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Nom" className="sensoria-input" />
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Description" rows={4} className="sensoria-input resize-none" />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                      placeholder="Ville" className="sensoria-input" />
                    <input type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="Adresse" className="sensoria-input" />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Téléphone" className="sensoria-input" />
                    <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                      placeholder="Site web" className="sensoria-input" />
                  </div>
                  <textarea value={form.opening_hours} onChange={(e) => setForm({ ...form, opening_hours: e.target.value })}
                    placeholder="Horaires d'ouverture" rows={2} className="sensoria-input resize-none" />
                  <input type="text" value={form.ticketing_url} onChange={(e) => setForm({ ...form, ticketing_url: e.target.value })}
                    placeholder="Lien billetterie / réservation" className="sensoria-input" />

                  {/* Photos */}
                  <div>
                    <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-sensoria-text-dim">Photos du lieu</label>
                    <PhotoUpload entityType="place" entityId={place.id} maxPhotos={10} />
                  </div>

                  <div className="flex gap-2">
                    <button onClick={() => handleSave(place.id)} disabled={saving} className="sensoria-btn px-5 py-2 text-sm">
                      {saving ? "Sauvegarde..." : "Sauvegarder"}
                    </button>
                    <button onClick={() => setEditing(null)} className="rounded-xl border border-sensoria-border px-5 py-2 text-sm text-sensoria-text-dim hover:text-white">
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                /* View mode */
                <div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="flex items-center gap-2 font-semibold text-white">
                        {place.name}
                        <span className="rounded-full bg-sensoria-success/15 px-2 py-0.5 text-[10px] font-semibold text-sensoria-success">✓ Vérifié</span>
                      </h3>
                      <p className="text-xs text-sensoria-text-dim">📍 {place.city}{place.address ? ` · ${place.address}` : ""}</p>
                    </div>
                    <button onClick={() => startEdit(place)} className="sensoria-btn px-4 py-1.5 text-sm">Modifier</button>
                  </div>
                  {place.description && <p className="mt-2 text-sm text-sensoria-text-dim">{place.description}</p>}
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-sensoria-muted">
                    {place.phone && <span>📞 {place.phone}</span>}
                    {place.website && <span>🔗 {place.website}</span>}
                    {place.opening_hours && <span>🕐 {place.opening_hours}</span>}
                    {place.ticketing_url && <span>🎫 Billetterie active</span>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="sensoria-card text-center py-8">
          <div className="mb-3 text-4xl">🏢</div>
          <p className="text-sensoria-text-dim">Aucun établissement revendiqué pour le moment.</p>
          <p className="mt-1 text-xs text-sensoria-muted">Rendez-vous dans l'annuaire des Lieux et cliquez sur "C'est mon établissement".</p>
        </div>
      )}

      {/* Pending claims */}
      {claims.filter((c) => c.status === "pending").length > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-semibold text-sensoria-text-dim">Demandes en attente</h3>
          <div className="space-y-2">
            {claims.filter((c) => c.status === "pending").map((claim) => (
              <div key={claim.id} className="sensoria-card flex items-center justify-between">
                <div>
                  <span className="font-semibold text-white">{claim.places?.name || "Lieu"}</span>
                  <p className="text-xs text-sensoria-muted">{claim.business_name} · Soumis le {new Date(claim.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-[10px] font-semibold text-amber-300">En attente</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
